/**
 * Blossom — filters.js
 * <facet-filters> — progressive enhancement for Shopify storefront filtering
 * and sorting on the collection and search pages (snippets/facets*.liquid).
 *
 * Any change in a filter form, the sort select or a remove/clear link
 * re-renders the section through the Section Rendering API instead of
 * reloading the page, then updates the URL (history.pushState) so results
 * are shareable and the back button works. Without JavaScript the forms and
 * links work as regular GET requests.
 *
 * Regions swapped after each request (all inside <facet-filters>):
 *   [data-facets-results]          product grid, empty states, pagination
 *   [data-facets-body="sidebar"]   desktop sidebar filter form
 *   [data-facets-body="drawer"]    drawer filter form
 *   [data-facets-drawer-footer]    drawer "Clear all" / "Show N results"
 *   [data-results-count]           results count text
 *   [data-active-facets]           active filter pills (replaced whole)
 *   [data-active-count]            active filter badge (replaced whole)
 *
 * Events: theme:facets:updated { url, sectionId }
 */
(() => {
  if (!window.Theme || customElements.get('facet-filters')) return;

  const INNER_REGIONS = [
    '[data-facets-results]',
    '[data-facets-body="sidebar"]',
    '[data-facets-body="drawer"]',
    '[data-facets-drawer-footer]',
    '[data-results-count]',
  ];
  const OUTER_REGIONS = ['[data-active-facets]', '[data-active-count]'];

  class FacetFilters extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.sectionId = this.dataset.sectionId;

      this.debouncedOptionChange = Theme.debounce((form) => this.onFormChange(form), 350);
      this.debouncedPriceChange = Theme.debounce((form) => this.onFormChange(form), 800);

      this.addEventListener('input', (event) => this.onInput(event));
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('submit', (event) => this.onSubmit(event));
      this.addEventListener('click', (event) => this.onClick(event));

      this.currentKey = this.locationKey(window.location.href);
      // Re-render when the back/forward buttons change the query string
      // (hash-only navigation, e.g. skip links, is ignored).
      this.onPopState = () => {
        if (this.locationKey(window.location.href) !== this.currentKey) {
          this.render(window.location.href, { push: false });
        }
      };
      window.addEventListener('popstate', this.onPopState);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      this.controller?.abort();
    }

    /* ------------------------------------------------------------ Events */
    /** Price inputs update while typing (debounced). */
    onInput(event) {
      const form = event.target.closest('[data-facets-form]');
      if (form && event.target.matches('[data-price-input]')) this.debouncedPriceChange(form);
    }

    onChange(event) {
      if (event.target.matches('[data-sort-select]')) {
        this.onSortChange(event.target.value);
        return;
      }
      const form = event.target.closest('[data-facets-form]');
      if (!form) return;
      // Checkboxes are batched briefly so several quick clicks make one request.
      if (event.target.matches('input[type="checkbox"]')) this.debouncedOptionChange(form);
      // Sort select inside the drawer form.
      else if (event.target.matches('select')) this.onFormChange(form);
    }

    onSubmit(event) {
      const form = event.target;
      if (form.matches('[data-facets-form]')) {
        event.preventDefault();
        this.onFormChange(form);
      } else if (form.matches('[data-sort-form]')) {
        event.preventDefault();
        const select = form.querySelector('select[name="sort_by"]');
        if (select) this.onSortChange(select.value);
      }
    }

    onClick(event) {
      const link = event.target.closest('a[data-facet-link]');
      if (link && link.href) {
        event.preventDefault();
        this.render(link.href);
        return;
      }
      const toggle = event.target.closest('[data-facets-show-more]');
      if (toggle) this.toggleShowMore(toggle);
    }

    onFormChange(form) {
      this.render(this.buildUrl(form));
    }

    onSortChange(value) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', value);
      url.searchParams.delete('page');
      this.render(url.toString());
    }

    /** Filter form → results URL. Empty values (e.g. price inputs) are skipped. */
    buildUrl(form) {
      const url = new URL(form.getAttribute('action') || window.location.pathname, window.location.origin);
      url.search = '';
      for (const [key, value] of new FormData(form)) {
        if (typeof value === 'string' && value.trim() === '') continue;
        url.searchParams.append(key, value);
      }
      return url.toString();
    }

    toggleShowMore(button) {
      const list = document.getElementById(button.getAttribute('aria-controls'));
      if (!list) return;
      const expanded = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', String(expanded));
      list.classList.toggle('is-expanded', expanded);
      button.textContent = expanded ? button.dataset.labelLess : button.dataset.labelMore;
    }

    locationKey(url) {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.delete('section_id');
      return parsed.pathname + parsed.search;
    }

    /* ------------------------------------------------------------ Render */
    async render(url, { push = true } = {}) {
      const key = this.locationKey(url);
      // Ignore duplicates, e.g. Enter in a price field followed by its debounce.
      if (push && key === this.currentKey && !this.controller) return;
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;

      const results = this.querySelector('[data-facets-results]');
      results?.classList.add('is-loading');
      results?.setAttribute('aria-busy', 'true');
      const state = this.captureState();

      try {
        const doc = await Theme.fetchSection(url, this.sectionId, { signal });
        this.swap(doc);
        this.restoreState(state);

        if (push) {
          const clean = new URL(url, window.location.origin);
          clean.searchParams.delete('section_id');
          window.history.pushState({ facets: this.sectionId }, '', clean.toString());
        }
        this.currentKey = key;

        const count = this.querySelector('[data-results-count]');
        if (count) Theme.announce(count.textContent.trim());
        this.scrollResultsIntoView();
        Theme.emit('theme:facets:updated', { url, sectionId: this.sectionId }, this);
      } catch (error) {
        if (error.name === 'AbortError') return;
        Theme.toast(this.dataset.errorMessage || error.message, { type: 'error' });
      } finally {
        if (!signal.aborted) {
          this.controller = null;
          const current = this.querySelector('[data-facets-results]');
          current?.classList.remove('is-loading');
          current?.removeAttribute('aria-busy');
        }
      }
    }

    swap(doc) {
      INNER_REGIONS.forEach((selector) => {
        const sources = doc.querySelectorAll(selector);
        this.querySelectorAll(selector).forEach((target, index) => {
          const source = sources[index];
          if (source) target.innerHTML = source.innerHTML;
        });
      });

      OUTER_REGIONS.forEach((selector) => {
        const sources = doc.querySelectorAll(selector);
        this.querySelectorAll(selector).forEach((target, index) => {
          const source = sources[index];
          if (source) target.replaceWith(document.importNode(source, true));
        });
      });

      // Keep the toolbar sort select in sync.
      const sortSource = doc.querySelector('[data-sort-select]');
      const sortTarget = this.querySelector('[data-sort-select]');
      if (sortSource && sortTarget) {
        const selected = sortSource.querySelector('option[selected]');
        if (selected) sortTarget.value = selected.value;
      }

      Theme.initReveal?.(this);
    }

    /** Remember open filters, focus and scroll positions across re-renders. */
    captureState() {
      const open = {};
      this.querySelectorAll('[data-facets-body]').forEach((body) => {
        body.querySelectorAll('details[data-filter-param]').forEach((details) => {
          open[`${body.dataset.facetsBody}:${details.dataset.filterParam}`] = details.open;
        });
      });
      const active = document.activeElement;
      const scroll = {};
      this.querySelectorAll('[data-facets-body]').forEach((body) => {
        scroll[body.dataset.facetsBody] = body.scrollTop;
      });
      return {
        open,
        scroll,
        focusId: active && this.contains(active) && active.id ? active.id : null,
        selection: active && typeof active.selectionStart === 'number' ? active.selectionStart : null,
      };
    }

    restoreState(state) {
      this.querySelectorAll('[data-facets-body]').forEach((body) => {
        const context = body.dataset.facetsBody;
        body.querySelectorAll('details[data-filter-param]').forEach((details) => {
          const key = `${context}:${details.dataset.filterParam}`;
          if (key in state.open) details.open = state.open[key];
        });
        if (state.scroll[context]) body.scrollTop = state.scroll[context];
      });

      if (state.focusId) {
        const element = document.getElementById(state.focusId);
        if (element) {
          element.focus({ preventScroll: true });
          if (state.selection !== null && typeof element.setSelectionRange === 'function') {
            try {
              element.setSelectionRange(state.selection, state.selection);
            } catch (error) {
              /* number inputs don't support selection ranges */
            }
          }
        }
      }
    }

    /** If the grid top scrolled out of view (and no drawer is open), bring it back. */
    scrollResultsIntoView() {
      if (document.documentElement.classList.contains('scroll-lock')) return;
      const results = this.querySelector('[data-facets-results]');
      if (!results) return;
      const offset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-offset')) || 0;
      const top = results.getBoundingClientRect().top;
      if (top < offset) {
        window.scrollTo({
          top: window.scrollY + top - offset - 24,
          behavior: Theme.prefersReducedMotion() ? 'auto' : 'smooth',
        });
      }
    }
  }

  customElements.define('facet-filters', FacetFilters);
})();
